import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { MemberType, PermissionsData } from '../MemberProfile.types'

import { CardDashboard, CardContent, Badge, Button, Flex } from '@percent/lemonade'
import { PermissionsList } from '@percent/domain/iam'
import { AssignRoleDialog } from '@percent/admin-dashboard/app/member/AssignRoleDialog/AssignRoleDialog'
import type { Account } from '@percent/domain/iam'

type RolesPermissionsCardProps = {
  account: Account
  memberType: MemberType
  permissions?: PermissionsData
  canChangeRole?: boolean
  onRefresh: () => void
}

export function RolesPermissionsCard({
  account,
  memberType,
  permissions,
  canChangeRole,
  onRefresh
}: RolesPermissionsCardProps) {
  const { t } = useTranslation()
  const [isChangeRoleOpen, setIsChangeRoleOpen] = useState(false)

  if (memberType === 'goodstack' && permissions) {
    return (
      <CardDashboard title={t('memberProfile.rolesPermissions')} headingLevel="h4">
        <PermissionsList
          title={t('memberProfile.selectTeamMemberRoles')}
          assignedDomains={permissions.assignedDomains}
          effectiveDomains={permissions.effectiveDomains}
          getGroupHref={permissions.getGroupHref}
          isLoading={permissions.isLoading}
          isDisabled={permissions.isDisabled}
          assignAccountRole={permissions.assignAccountRole}
          unassignAccountRole={permissions.unassignAccountRole}
        />
      </CardDashboard>
    )
  }

  const { partnerId } = account

  return (
    <>
      <CardDashboard
        title={t('memberProfile.rolesPermissions')}
        headingLevel="h4"
        action={
          canChangeRole && partnerId ? (
            <Button variant="secondary" size="small" onClick={() => setIsChangeRoleOpen(true)}>
              {t('memberProfile.changeRole')}
            </Button>
          ) : undefined
        }
      >
        <CardContent title={t('memberProfile.currentRole')} testId="role-name">
          <Flex direction="row" align="center" gap="sm">
            <Badge variant="default">{account.role?.name ?? t('typography.member')}</Badge>
          </Flex>
        </CardContent>
      </CardDashboard>
      {partnerId && (
        <AssignRoleDialog
          open={isChangeRoleOpen}
          account={
            isChangeRoleOpen
              ? {
                  id: account.id,
                  email: account.email,
                  currentRoleName: account.role?.name || t('typography.member')
                }
              : undefined
          }
          partnerId={partnerId}
          refresh={() => {
            onRefresh()
            setIsChangeRoleOpen(false)
          }}
          onClose={() => setIsChangeRoleOpen(false)}
        />
      )}
    </>
  )
}
